import { memo, useCallback, useMemo } from "react";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Button } from "@mui/material";
import LandingScreenHeader from "../../components/Comman/LandingScreenHeader";
import tableStyles from "../../Styles/csvImportingValidation";
import {
    checkboxColor,
    lightPinkColor,
    offWhiteColor,
    white,
} from "../../config/ColorObj";
import {
    importHolidayStructureAssignationMsg,
    noDataFoundMsg,
} from "../../config/messageconstant";

// ── Stable style objects defined OUTSIDE the component ──────────────────────
// Inline objects created inside render cause child re-renders every cycle.

const exportBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.2s",
    minWidth: "120px",
    height: "35px",
    "&:hover": {
        backgroundColor: offWhiteColor,
        borderColor: checkboxColor,
        color: checkboxColor,
        transform: "translateY(-2px)",
    },
};

const importBtnSx = {
    color: white,
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.2s",
    minWidth: "150px",
    height: "35px",
    "&:hover": {
        backgroundColor: checkboxColor,
        transform: "translateY(-2px)",
    },
};

const headerWrapStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
};

const btnGroupStyle = { display: "flex", gap: 15 };

const searchWrapStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "5px",
};

const searchInputStyle = {
    padding: "6px 10px",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    width: "230px",
};

const errorCellStyle = { ...tableStyles.dataCell, minWidth: "90px", textAlign: "left" };
const srNoCellStyle = { ...tableStyles.dataCell, minWidth: "60px", textAlign: "center" };
const empCodeStyle = { ...tableStyles.dataCell, minWidth: "80px", textAlign: "center" };
const hsNameStyle = { ...tableStyles.dataCell, minWidth: "80px", textAlign: "left" };

// ── Memoised row to prevent re-renders when siblings change ─────────────────
const TableRow = memo(({ row, index }) => {
    const message = row.errorMsgs.join(", ");
    const isError = row.flag === 1;

    const baseStyle = {
        ...tableStyles.dataRow,
        ...(index % 2 === 0 ? tableStyles.alternateRow : {}),
        ...(isError && { backgroundColor: lightPinkColor }),
    };

    const handleMouseEnter = useCallback((e) => {
        e.currentTarget.style.backgroundColor =
            tableStyles.dataRowHover.backgroundColor;
    }, []);

    const handleMouseLeave = useCallback(
        (e) => {
            e.currentTarget.style.backgroundColor = isError
                ? lightPinkColor
                : index % 2 === 0
                    ? tableStyles.alternateRow.backgroundColor
                    : tableStyles.dataRow.backgroundColor;
        },
        [isError, index]
    );

    return (
        <tr
            // Use a stable, unique id from your data instead of array index whenever
            // possible — index keys force React to unmount/remount on sort/filter.
            key={row.id ?? index}
            style={baseStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <td style={srNoCellStyle}>{index + 1}</td>
            <td style={empCodeStyle}>{row?.salesTeamEmployeeCode ?? "-"}</td>
            <td style={hsNameStyle}>{row?.holidayStructureHeaderName ?? "-"}</td>
            <td style={errorCellStyle}>{isError ? message || "-" : "-"}</td>
        </tr>
    );
});

TableRow.displayName = "TableRow";

// ── Main component ───────────────────────────────────────────────────────────
const HolidayStructureAssignationTable = memo(({
    onClickOfExport,
    onClickOfImport,
    searchValue,
    onSearchBox,
    getFilteredTableData,
    validatedDataList,
}) => {
    // Re-compute only when the list or search term changes — not on every render.
    const filteredData = useMemo(
        () =>
            validatedDataList.length > 0
                ? getFilteredTableData(validatedDataList)
                : [],
        [validatedDataList, getFilteredTableData]
    );

    return (
        <>
            <div style={headerWrapStyle}>
                <LandingScreenHeader
                    screenTitle={importHolidayStructureAssignationMsg}
                    showSaveBtnMain={false}
                    showAddButton={false}
                    showBackButton={false}
                />
                <div style={btnGroupStyle}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        startIcon={<CloudDownloadIcon />}
                        sx={exportBtnSx}
                        onClick={onClickOfExport}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={<CloudUploadIcon sx={{ color: white }} />}
                        sx={importBtnSx}
                        onClick={onClickOfImport}
                    >
                        Import
                    </Button>
                </div>
            </div>

            <br />

            <div style={searchWrapStyle}>
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={onSearchBox}
                    style={searchInputStyle}
                />
            </div>

            <div style={tableStyles.tableWrapper}>
                <table style={tableStyles.table}>
                    <thead style={tableStyles.stickyHeader}>
                        <tr style={tableStyles.headerRow}>
                            <th style={{ ...tableStyles.headerCell, minWidth: "60px" }}>Sr.No</th>
                            <th style={{ ...tableStyles.headerCell, minWidth: "80px", textAlign: "center" }}>
                                Employee Code
                            </th>
                            <th style={{ ...tableStyles.headerCell, minWidth: "80px", textAlign: "left" }}>
                                Holiday Structure Name
                            </th>
                            <th style={{ ...tableStyles.headerCell, minWidth: "130px", textAlign: "left" }}>
                                Error Message
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length > 0 ? (
                            filteredData.map((row, index) => (
                                <TableRow key={row.id ?? index} row={row} index={index} />
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={tableStyles.noData}>
                                    {noDataFoundMsg}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
});

HolidayStructureAssignationTable.displayName = "HolidayStructureAssignationTable";

export default HolidayStructureAssignationTable;